import { Component, ElementRef, HostListener, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { animate, state, style, transition, trigger } from "@angular/animations";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { UntypedFormGroup } from "@angular/forms";
import { FormService } from "../../services/form.service";

interface vcItem {
	name: string,
	volume: number,
	duplicate?: boolean,
}
type vcItems = vcItem[];
interface vcCategory {
	category: string,
	svg: string,
	items: vcItems,
}
type vcCategories = vcCategory[];

type vcOutputCategory = {
	[key: string]: any
}
type vcOutputCategories = {
	[key: string]: vcOutputCategory
}
interface vcSearchResult {
	name: string,
	catIndex: number,
	itemIndex: number,
	catName: string,

	// Duplicates are for the search functionality
	// This way we can include the category otherwise it will show the same item twice
	duplicate?: boolean,
}
type vcSearchResults = vcSearchResult[];

@Component( {
	selector: 'app-volume-calculator',
	templateUrl: './volume-calculator.component.html',
	styleUrls: ['./volume-calculator.component.scss'],
	animations: [
		// Used for the vc itself
		trigger( 'fadeInOut', [
			transition( ':enter', [
				style( {opacity: 0} ),
				animate( '300ms ease-in-out', style( {opacity: 1} ) )
			] ),
			transition( ':leave', [
				style( {opacity: 1} ),
				animate( '300ms ease-in-out', style( {opacity: 0} ) )
			] ),
		] ),
	]
} )
export class VolumeCalculatorComponent implements OnInit {

	volumeCalculatorForm !: UntypedFormGroup
	formGroupName : string = 'volumeCalculatorForm'

	public activeCat: number = 0;
	public activeCatMobile: number = 9999;
	public vcCategories: vcCategories | undefined;
	public vcPreCategories: any | undefined;
	public vcSearchResults: vcSearchResults = [];
	public vcM3: string = '0';
	public vcFt3: string = '0';
	@ViewChild( 'vcBody' ) vcBody!: ElementRef;
	@ViewChild( 'vcSearchRef' ) vcSearchRef!: ElementRef;
	@ViewChild( 'vcSearchInput' ) vcSearchInput!: ElementRef;

	constructor( public formService: FormService,
	             @Inject(MAT_DIALOG_DATA) data: { vcCategories: vcCategories, vcPreCategories: any } ) {

		// Get data from parent
		this.vcCategories = data.vcCategories;
		this.vcPreCategories = data.vcPreCategories;
	}

	ngOnInit(): void {

		this.volumeCalculatorForm = this.formService.volumeCalculatorForm as UntypedFormGroup;

		this.handlePreItems();

		// Re-calculate for when the user re-opens the vc
		this.calculateTotal();
	}

	private handlePreItems() {

		if( typeof this.vcPreCategories === 'undefined' ) {

			return;
		}

		// The pre-filled items and the item list are fetched separately, so we can have one
		// without the other — the item list is fetched on entering this step and the user may
		// open the calculator before it lands, or it may have failed. calculateTotal() below
		// already guards the same way.
		if( ! this.vcCategories ) {

			return;
		}

		// Go through vc categories to set the pre categories
		for( var [catIndex, category] of this.vcCategories.entries() ) {

			var categoryName = category.category;

			// Check if category exists in pre items
			if( this.vcPreCategories[categoryName] ) {

				// Loop categories
				for( var [itemIndex, item] of category.items.entries() ) {

					var itemName = item.name;

					// Check if item exists in pre items
					if( this.vcPreCategories[categoryName][itemName] ) {

						var controlId = catIndex + '-' + itemIndex;
						this.volumeCalculatorForm.controls[controlId].setValue( this.vcPreCategories[categoryName][itemName] );
					}
				}
			}
		}
	}

	public activateCat( cat: number ) {

		// Scroll to top of vc body
		this.vcBody.nativeElement.scrollTo( 0, 0 );
		this.activeCat = cat;
	}

	public toggleCatMobile( cat: number ) {

		// Toggle
		if( this.activeCatMobile == cat ) {

			// Reset
			this.activeCatMobile = 9999;

		} else {

			// Also set items for desktop
			this.activeCat = cat;
			this.activeCatMobile = cat;

			// Slide down
			var scrollContainer = this.vcBody.nativeElement;
			var outerOffset = scrollContainer.offsetTop;

			// Timeout to do the scrolling after the items are visible
			setTimeout( function() {

				var element = document.getElementById("vc-cat-mobile-" + cat );
				if( ! element ) {

					return;
				}

				var itemOffset = element.offsetTop;
				var newPos = itemOffset - outerOffset;
				scrollContainer.scrollTo({ top: newPos, behavior: 'smooth' });
			} );
		}
	}

	public itemUp( categoryIndex: number, itemIndex: number ) {

		var controlId = categoryIndex + '-' + itemIndex;
		var value = this.volumeCalculatorForm.controls[controlId].value;

		// Add 1
		this.volumeCalculatorForm.controls[controlId].setValue( ++value );

		this.calculateTotal();
	}

	public itemUpInit( categoryIndex: number, itemIndex: number ) {

		// For when clicking on the item title
		if( this.itemHasValue( categoryIndex, itemIndex ) ) {

			return;
		}

		this.itemUp( categoryIndex, itemIndex );
	}

	public itemDown( categoryIndex: number, itemIndex: number ) {

		var controlId = categoryIndex + '-' + itemIndex;
		var value = this.volumeCalculatorForm.controls[controlId].value;

		// If current value is 0 or less
		if( value <= 0 ) {
			return;
		}

		// Subtract 1
		this.volumeCalculatorForm.controls[controlId].setValue( --value );
		this.calculateTotal();
	}

	public itemInput( event: any, categoryIndex: number, itemIndex: number ) {

		var controlId = categoryIndex + '-' + itemIndex;
		var value = Number( event.target.value );

		value = ( value < 0 ? 0 : value );

		this.volumeCalculatorForm.controls[controlId].setValue( value );
		this.volumeCalculatorForm.updateValueAndValidity();
		this.calculateTotal();
	}

	public itemHasValue( categoryIndex: number, itemIndex: number  ) {

		var controlId = categoryIndex + '-' + itemIndex;
		var value = this.volumeCalculatorForm.controls[controlId].value;

		return ( value > 0 );
	}

	public calculateTotal() {

		if( ! this.vcCategories ) {

			return;
		}

		var totalVolume = 0;
		var ft3Convert = 35.3146667;
		var selectedItems : vcOutputCategories = {};

		for( var [catIndex,category] of this.vcCategories.entries() ) {

			var categoryName = category.category;

			for( var [itemIndex,item] of category.items.entries() ) {

				var controlId = catIndex + '-' + itemIndex;
				var itemName = item.name;
				var amount = this.volumeCalculatorForm.controls[controlId].value;

				if( amount > 0 && category.category ) {

					// If category name not in the list yet
					if( ! ( categoryName as keyof vcOutputCategories in selectedItems ) ) {

						selectedItems[categoryName as keyof vcOutputCategories] = {};
					}

					// Set item with amount
					selectedItems[categoryName][itemName as keyof vcOutputCategory] = amount;

					// Volume x amount added to total
					totalVolume += item.volume * amount;
				}
			}
		}

		// toFixed returns a string
		var totalFt3 : string = ( totalVolume * ft3Convert ).toFixed( 1 );
		var totalM3 : string = ( totalVolume ).toFixed( 1 );

		this.setResult( totalM3, totalFt3 );

		// Set volume field
		(<UntypedFormGroup>this.formService.mainForm.controls['movingContent']).controls['volumeM3'].setValue( totalM3 );
		(<UntypedFormGroup>this.formService.mainForm.controls['movingContent']).controls['volumeFt3'].setValue( totalFt3 );

		// Set vc field
		(<UntypedFormGroup>this.formService.mainForm.controls['movingContent']).controls['_volumeCalculator'].setValue( JSON.stringify( selectedItems) );

		return;
	}

	public setResult( m3: string, ft3: string ) {

		this.vcM3 = m3;
		this.vcFt3 = ft3;
	}

	// == SEARCH == //
	public vcSearch( event: any ) {

		if( ! this.vcCategories ) {

			return;
		}

		// Ignore enter
		//

		// Clear results

		var query : string = event.target.value;

		if( query.length === 0 ) {

			// Empty results list
			this.vcSearchResults = [];

			return;
		}

		query = query.toLowerCase();

		// Create search array
		var queryArray = query.split( ' ' );

		// Get search string length
		var queryLength = queryArray.length;

		// Clear first
		this.vcSearchResults = [];

		// Loop over complete list
		for( var [catIndex,category] of this.vcCategories.entries() ) {

			for ( var [itemIndex, item] of category.items.entries() ) {

				var oneLine = item.name.toLowerCase(),
					match = true,
					sal = queryLength;

				// Loop length amount of times
				while( sal-- ) {

					// If line does not exist in search query
					if( oneLine.indexOf( queryArray[sal] ) === -1 ) {

						// Set match to false
						match = false;
					}
				}

				// This item should be shown for the current query
				if( match ) {

					var searchItem : vcSearchResult = {

						name: item.name,
						itemIndex: itemIndex,
						catName: category.category,
						catIndex: catIndex,
					}

					if( item.duplicate ) {

						searchItem.duplicate = true;
					}

					// Add to vcSearchResults
					this.vcSearchResults.push( searchItem );
				}
			}
		}
	}

	@HostListener('document:click', ['$event'])
	public vcHandleClicks(event: any) {

		if( ! this.vcSearchRef ) {

			return;
		}

		// Clicking outside the search filter
		if( ! this.vcSearchRef.nativeElement.contains(event.target) ) {

			// Clear search input value
			this.vcSearchInput.nativeElement.value = '';

			// If there are search results
			if( this.vcSearchResults.length ) {

				this.vcSearchResults = [];
			}
		}
	}
}